import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { PILLARS, ARTICLES } from "@/data/articles";

export default function JournalHubPage() {
  const [pillar, setPillar] = useState("all");
  const filtered = pillar === "all" ? ARTICLES : ARTICLES.filter((a) => a.pillar === pillar);
  const [featured, ...rest] = filtered;

  useEffect(() => {
    document.title = "The Clarity Journal — Plain Writing for Busy Owners | Ocean Data";
  }, []);

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]">
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            The Clarity Journal
          </p>
          <h1
            data-testid="journal-headline"
            className="font-display font-bold tracking-tight leading-[1.04] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="journal-headline-line-1">Plain writing for owners</MaskedLine>
            <MaskedLine delay={0.3} testId="journal-headline-line-2">
              <span className="text-brand-teal">tired of chasing information.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            Short, honest pieces across four obsessions: more capacity, one
            source of truth, simpler processes, and the quiet technology
            underneath — always in that order.
          </motion.p>

          <div data-testid="journal-pillar-filters" className="flex flex-wrap gap-2.5 mt-12 mb-14">
            {[{ id: "all", label: "All" }, ...PILLARS].map((p) => (
              <button
                key={p.id}
                data-testid={`journal-filter-${p.id}`}
                onClick={() => setPillar(p.id)}
                aria-pressed={pillar === p.id}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                  pillar === p.id
                    ? "bg-brand-navy text-white shadow"
                    : "bg-white border border-slate-200 text-slate-600 hover:border-brand-teal hover:text-brand-navy"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {featured && (
            <Reveal>
              <Link
                to={`/journal/${featured.slug}`}
                data-testid="journal-featured-card"
                className="group block rounded-3xl bg-brand-navy text-white p-8 sm:p-12 mb-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-20px_rgba(14,20,51,0.5)]"
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-10 h-10 rounded-full bg-brand-teal/15 text-brand-teal flex items-center justify-center">
                    <BookOpen size={17} />
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
                    Featured · {PILLARS.find((p) => p.id === featured.pillar)?.label} · {featured.readTime}
                  </span>
                </div>
                <h2 className="font-display font-semibold text-2xl sm:text-3xl lg:text-4xl leading-snug mb-4 max-w-3xl group-hover:text-brand-tealsoft transition-colors">
                  {featured.title}
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed max-w-2xl mb-7">
                  {featured.excerpt}
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-brand-gold">
                  Read it now
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7">
            {rest.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.07} className="h-full">
                <Link
                  to={`/journal/${a.slug}`}
                  data-testid={`journal-card-${a.slug}`}
                  className="group flex flex-col h-full rounded-2xl bg-white border border-slate-200 p-7 transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-teal font-semibold mb-4">
                    {PILLARS.find((p) => p.id === a.pillar)?.label} · {a.readTime}
                  </span>
                  <h3 className="font-display font-semibold text-lg sm:text-xl text-brand-navy leading-snug mb-3 group-hover:text-brand-teal transition-colors">
                    {a.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed flex-1">{a.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-teal">
                    Read
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="mt-14 text-sm text-slate-500 max-w-2xl leading-relaxed">
              Prefer to feel it instead of read it?{" "}
              <Link to="/demos" className="font-semibold text-brand-teal hover:text-brand-navy transition-colors">
                The live demos
              </Link>{" "}
              show the same ideas working on sample data.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
